import { isTransparentFill } from '../lib/color';
import { FILL_TYPES, TYPES } from './constants';
import { drawShadow } from './effects/shadow';
import { loadImageCache, getNodeImageCache } from '../provider/image';
import { renderNode } from './node';
import { drawBackground, resolveFillType } from './utils/background';
import { renderLogo } from './utils/logo';
import {
  createRasterContext,
  getCacheByType,
  hasDynamicNodeInSubtree,
  isHighRasterCost,
} from './utils/raster';
import { getNodeRenderBounds } from './utils/node';

export function renderFrame(stageContext, node) {
  if (node.type !== TYPES.frame) return;
  const renderNodes = stageContext.api.getChildren(node.id);

  // Decide whether to cache (optimized for small thumbnails)
  const hasDynamicNode = hasDynamicNodeInSubtree(stageContext, node);
  const highCost = isHighRasterCost(stageContext, { w: node.w, h: node.h });
  const tooFewChildren = renderNodes.length < 2;
  // Thumbnail heuristic: allow caching even for single-child frames when the frame is small
  const isSmallThumb = Math.max(node.w || 0, node.h || 0) <= 400;
  const isLoadingAnimation = !renderNodes.length && node.loading;
  const isImage = resolveFillType(node.fill) === FILL_TYPES.IMAGE;
  let isImageLoading = false;
  if (isImage) {
    loadImageCache(stageContext, node);
    const imgCache = getNodeImageCache(stageContext, node.id);
    isImageLoading = imgCache?.loading ?? false;
  }
  const shouldCache =
    !isImageLoading &&
    !isLoadingAnimation &&
    !hasDynamicNode &&
    (isSmallThumb || !tooFewChildren) &&
    !highCost &&
    stageContext.mode !== 'export';
  if (shouldCache) {
    renderFrameWithCache(stageContext, node, renderNodes);
  } else {
    renderFrameDirect(stageContext, node, renderNodes);
  }
}

function renderFrameWithCache(stageContext, node, renderNodes) {
  const { context } = stageContext;
  const cacheName = node.id;
  const caches = getCacheByType(stageContext, 'renderFrame');
  const existCache = caches.get(cacheName);
  const { scale, dirty: dirtyProvider } = stageContext.providers || {};

  const deviceDPR = stageContext.dpr || 1;
  const safeDPR = Math.max(2, deviceDPR);
  const zoom = (scale?.scale || 1) * safeDPR;

  // 使用细粒度的脏检测 - 获取所有后代节点的版本，而不仅仅是直接子节点
  const descendants = stageContext.api?.getDescendants
    ? stageContext.api.getDescendants(node.id)
    : renderNodes;
  const descendantIds = descendants.map((n) => n.id);
  const subtreeVersion = dirtyProvider.getSubtreeVersion(node.id, descendantIds);

  // 检查 Frame 自身是否只有位置变化（背景等内容没变）
  const frameContentKey = dirtyProvider.getContentKey(node.id);
  const frameTransformKey = dirtyProvider.getTransformKey(node.id);

  // Use relative zoom tolerance to handle different zoom levels better
  // Cache is valid if zoom ratio is within 5% (0.95 to 1.05)
  const zoomRatio = existCache ? existCache.zoom / zoom : 0;
  const isZoomMatch = existCache && zoomRatio >= 0.95 && zoomRatio <= 1.05;
  const isScaling = !!scale?.scaling;

  // 完整缓存有效性检查
  // During active scaling, don't reuse cache to prevent blur from resolution mismatch
  const isFullCacheValid =
    existCache &&
    existCache.subtreeVersion === subtreeVersion &&
    existCache.frameContentKey === frameContentKey &&
    existCache.canvas &&
    existCache.renderConfig &&
    existCache.renderConfig.actualWidth > 0 &&
    existCache.renderConfig.actualHeight > 0 &&
    isZoomMatch &&
    !isScaling; // Never reuse cache during scaling to prevent blur

  if (isFullCacheValid) {
    if (stageContext.highQuality) {
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
    }

    // 检查是否只是 Frame 位置变化
    const deltaX = node.x - existCache.nodeX;
    const deltaY = node.y - existCache.nodeY;

    context.drawImage(
      existCache.canvas,
      0,
      0,
      existCache.renderConfig.actualWidth,
      existCache.renderConfig.actualHeight,
      existCache.x + deltaX,
      existCache.y + deltaY,
      existCache.w,
      existCache.h
    );

    // 更新缓存中的位置信息
    if (existCache.frameTransformKey !== frameTransformKey) {
      existCache.nodeX = node.x;
      existCache.nodeY = node.y;
      existCache.x += deltaX;
      existCache.y += deltaY;
      existCache.frameTransformKey = frameTransformKey;
    }

    if (node.seedeLogo) {
      renderLogo(stageContext, node);
    }

    return;
  }

  // 检查是否可以增量更新（只有部分子节点变化）
  // Don't allow incremental update during scaling to prevent blur
  const canIncrementalUpdate =
    existCache &&
    existCache.canvas &&
    existCache.childVersions &&
    existCache.frameContentKey === frameContentKey &&
    isZoomMatch &&
    !isScaling;

  if (canIncrementalUpdate) {
    const dirtyChildIds = dirtyProvider.getDirtyChildIds(descendantIds, existCache.childVersions);

    // Map nested dirty nodes to their direct child ancestors
    // This ensures that if a grandchild (e.g., text inside group) changes,
    // we redraw the entire direct child (e.g., the group) that contains it
    const directChildIdSet = new Set(renderNodes.map((n) => n.id));
    const effectiveDirtyIds = new Set();

    for (const dirtyId of dirtyChildIds) {
      if (directChildIdSet.has(dirtyId)) {
        // Direct child - add directly
        effectiveDirtyIds.add(dirtyId);
      } else {
        // Nested node - find its direct child ancestor
        let current = stageContext.api.getNode(dirtyId);
        while (current && current.parentId) {
          if (current.parentId === node.id) {
            // current is a direct child of the frame
            effectiveDirtyIds.add(current.id);
            break;
          }
          current = stageContext.api.getNode(current.parentId);
        }
      }
    }

    const effectiveDirtyArray = Array.from(effectiveDirtyIds);
    // 如果只有少量子节点变化（<30%），使用增量更新
    if (effectiveDirtyArray.length > 0 && effectiveDirtyArray.length < renderNodes.length * 0.3) {
      performIncrementalUpdate(
        stageContext,
        node,
        renderNodes,
        existCache,
        effectiveDirtyArray,
        zoom
      );

      // 更新缓存版本和位置信息
      existCache.subtreeVersion = subtreeVersion;
      for (const childId of dirtyChildIds) {
        existCache.childVersions.set(childId, dirtyProvider.getVersion(childId));
        // 更新子节点的实际渲染边界（已在 performIncrementalUpdate 中处理直接子节点）
        // 这里处理嵌套节点的位置更新
        const childNode = stageContext.api.getNode(childId);
        if (childNode && existCache.childPositions) {
          const bounds = getNodeRenderBounds(stageContext, childNode);
          existCache.childPositions.set(childId, {
            x: bounds.x,
            y: bounds.y,
            w: bounds.w,
            h: bounds.h,
          });
        }
      }

      // 绘制到主画布
      if (stageContext.highQuality) {
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
      }

      context.drawImage(
        existCache.canvas,
        0,
        0,
        existCache.renderConfig.actualWidth,
        existCache.renderConfig.actualHeight,
        existCache.x,
        existCache.y,
        existCache.w,
        existCache.h
      );

      if (node.seedeLogo) {
        renderLogo(stageContext, node);
      }

      return;
    }
  }

  // 完整重绘
  if (existCache && existCache.canvas) {
    existCache.canvas.release();
  }

  const cache = {
    subtreeVersion,
    frameContentKey,
    frameTransformKey,
    zoom,
    canvas: null,
    nodeX: node.x,
    nodeY: node.y,
    x: node.x,
    y: node.y,
    w: node.w,
    h: node.h,
    childVersions: new Map(),
    // 存储每个子节点的位置信息，用于增量更新时清除旧位置
    childPositions: new Map(),
    dispose: () => {
      cache.canvas?.release();
    },
  };

  // 记录每个后代节点的版本（包括嵌套节点）
  for (const desc of descendants) {
    cache.childVersions.set(desc.id, dirtyProvider.getVersion(desc.id));
    // 记录子节点的实际渲染边界，用于增量更新时清除旧位置（考虑文字溢出等）
    const bounds = getNodeRenderBounds(stageContext, desc);
    cache.childPositions.set(desc.id, { x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h });
  }

  // For frames, we always clip to the frame rect; raster exactly the frame bounds
  const rasterNode = { x: node.x, y: node.y, w: node.w, h: node.h };
  const {
    canvas,
    context: cacheContext,
    renderConfig,
  } = createRasterContext(stageContext, rasterNode, zoom);
  cache.canvas = canvas;
  cache.renderConfig = renderConfig;

  // Render content into cache
  cacheContext.save();
  const fakeStageContext = {
    ...stageContext,
    isNodeInViewport: () => true,
    context: cacheContext,
    _renderingToCache: true,
    // Provide rasterization metadata so effects (e.g., backdrop) can correctly
    // compute sampling coordinates inside the cache canvas.
    renderConfig,
    _rasterOrigin: { x: rasterNode.x, y: rasterNode.y },
    _rasterWorldToCacheOffset: { x: 0, y: 0 },
  };
  renderFrameContent(fakeStageContext, node, renderNodes);
  cacheContext.restore();

  caches.set(cacheName, cache);

  // Draw cached content to main canvas
  if (stageContext.highQuality) {
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
  }

  context.drawImage(
    cache.canvas,
    0,
    0,
    renderConfig.actualWidth,
    renderConfig.actualHeight,
    cache.x,
    cache.y,
    cache.w,
    cache.h
  );

  if (node.seedeLogo) {
    // Render logo in frame-local coordinates
    context.save();
    context.translate(node.x, node.y);
    renderLogo(stageContext, node);
    context.restore();
  }
}

/**
 * 增量更新：只重绘变脏的子节点
 */
function performIncrementalUpdate(
  stageContext,
  frameNode,
  renderNodes,
  cache,
  dirtyChildIds,
  zoom
) {
  const cacheContext = cache.canvas.getContext('2d');
  const { renderConfig } = cache;
  const dirtyIdSet = new Set(dirtyChildIds);

  // 计算需要重绘的区域
  const dirtyNodes = renderNodes.filter((n) => dirtyIdSet.has(n.id));

  const scaleX = renderConfig.scaleX;
  const scaleY = renderConfig.scaleY;

  // 添加一些边距以处理阴影等效果
  const margin = 10 * Math.max(scaleX, scaleY);

  // 对每个脏节点，清除其区域并重绘
  for (const dirtyNode of dirtyNodes) {
    // 获取节点的旧渲染边界（如果有的话）
    const oldBounds = cache.childPositions?.get(dirtyNode.id);

    // 计算节点的当前实际渲染边界（考虑文字溢出等）
    const currentBounds = getNodeRenderBounds(stageContext, dirtyNode);

    // 计算节点在缓存画布中的当前位置（使用实际渲染边界）
    const nodeX = currentBounds.x * scaleX;
    const nodeY = currentBounds.y * scaleY;
    const nodeW = currentBounds.w * scaleX;
    const nodeH = currentBounds.h * scaleY;

    // 清除旧位置和新位置的区域（在 cache 画布坐标系中，无变换）
    cacheContext.save();
    cacheContext.setTransform(1, 0, 0, 1, 0, 0); // 确保无变换，直接操作 cache 画布

    // 先清除旧位置（如果存在且与新位置不同）
    if (oldBounds) {
      const oldX = oldBounds.x * scaleX;
      const oldY = oldBounds.y * scaleY;
      const oldW = oldBounds.w * scaleX;
      const oldH = oldBounds.h * scaleY;

      // 只有当位置发生变化时才清除旧位置
      if (oldX !== nodeX || oldY !== nodeY || oldW !== nodeW || oldH !== nodeH) {
        cacheContext.clearRect(oldX - margin, oldY - margin, oldW + margin * 2, oldH + margin * 2);
      }
    }

    // 清除新位置
    cacheContext.clearRect(nodeX - margin, nodeY - margin, nodeW + margin * 2, nodeH + margin * 2);

    // 计算需要重绘的合并区域（包含旧位置和新位置）
    let clipX = nodeX - margin;
    let clipY = nodeY - margin;
    let clipW = nodeW + margin * 2;
    let clipH = nodeH + margin * 2;

    if (oldBounds) {
      const oldX = oldBounds.x * scaleX;
      const oldY = oldBounds.y * scaleY;
      const oldW = oldBounds.w * scaleX;
      const oldH = oldBounds.h * scaleY;

      // 合并旧位置和新位置的区域
      const minX = Math.min(clipX, oldX - margin);
      const minY = Math.min(clipY, oldY - margin);
      const maxX = Math.max(clipX + clipW, oldX + oldW + margin);
      const maxY = Math.max(clipY + clipH, oldY + oldH + margin);
      clipX = minX;
      clipY = minY;
      clipW = maxX - minX;
      clipH = maxY - minY;
    }

    // 设置裁剪区域（合并后的区域）
    cacheContext.beginPath();
    cacheContext.rect(clipX, clipY, clipW, clipH);
    cacheContext.clip();

    // 应用与 createRasterContext 相同的变换，以便 renderNode 能正确绘制
    // createRasterContext 设置: scale(scaleX, scaleY) + translate(-frameNode.x, -frameNode.y)
    // renderFrameContent 会 translate(frameNode.x, frameNode.y)，合并后等于 scale(scaleX, scaleY)
    cacheContext.scale(scaleX, scaleY);

    // 创建临时上下文用于重绘
    const fakeStageContext = {
      ...stageContext,
      isNodeInViewport: () => true,
      context: cacheContext,
      _renderingToCache: true,
      renderConfig,
      _rasterOrigin: { x: frameNode.x, y: frameNode.y },
      _rasterWorldToCacheOffset: { x: 0, y: 0 },
    };

    // 先重绘背景（如果 frame 有背景填充）
    if (frameNode.fill) {
      drawBackground(fakeStageContext, {
        ...frameNode,
        x: 0,
        y: 0,
      });
    }

    // 用于检查重叠的扩展脏区域（包含旧位置和新位置，使用实际渲染边界）
    const extendedDirtyNode = oldBounds
      ? {
          x: Math.min(currentBounds.x, oldBounds.x) - margin / scaleX,
          y: Math.min(currentBounds.y, oldBounds.y) - margin / scaleY,
          w:
            Math.max(currentBounds.x + currentBounds.w, oldBounds.x + oldBounds.w) -
            Math.min(currentBounds.x, oldBounds.x) +
            (margin * 2) / scaleX,
          h:
            Math.max(currentBounds.y + currentBounds.h, oldBounds.y + oldBounds.h) -
            Math.min(currentBounds.y, oldBounds.y) +
            (margin * 2) / scaleY,
        }
      : currentBounds;

    // 重绘所有与清除区域重叠的节点（包括上层节点）
    // 需要按照原始渲染顺序重绘，以保持正确的层叠关系
    for (let i = 0; i < renderNodes.length; i++) {
      const n = renderNodes[i];
      // 获取节点的实际渲染边界用于重叠检测
      const nodeBounds = getNodeRenderBounds(stageContext, n);
      // 检查是否与脏区域重叠（使用扩展的脏区域和实际渲染边界）
      if (nodesOverlap(nodeBounds, extendedDirtyNode, margin / scaleX)) {
        renderNode(fakeStageContext, n);
      }
    }

    cacheContext.restore();

    // 更新缓存中的渲染边界信息（使用实际渲染边界）
    if (cache.childPositions) {
      cache.childPositions.set(dirtyNode.id, {
        x: currentBounds.x,
        y: currentBounds.y,
        w: currentBounds.w,
        h: currentBounds.h,
      });
    }
  }
}

/**
 * 检查两个节点是否重叠
 */
function nodesOverlap(node1, node2, margin = 0) {
  return !(
    node1.x + node1.w + margin < node2.x - margin ||
    node2.x + node2.w + margin < node1.x - margin ||
    node1.y + node1.h + margin < node2.y - margin ||
    node2.y + node2.h + margin < node1.y - margin
  );
}

function renderFrameDirect(stageContext, node, renderNodes) {
  renderFrameContent(stageContext, node, renderNodes);
}

function renderFrameContent(stageContext, node, renderNodes) {
  const { context } = stageContext;
  const { x, y, w, h } = node;
  context.translate(x, y);

  const fill = node.fill;

  let seedeBadge = null;
  const nonBadgeNodes = renderNodes.filter((node) => !node.isSeedeBadge);
  if (!nonBadgeNodes.length && node.loading) {
    renderLoading(stageContext, node);
  } else {
    if (isTransparentFill(fill) && !renderNodes.length) {
      drawEmptyState(stageContext, w, h);
    } else {
      if (stageContext.mode !== 'export') {
        drawShadow(
          stageContext,
          {
            x: 0,
            y: 0,
            w,
            h,
            shadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
          },
          () => {
            context.fillStyle = '#fff';
            context.fillRect(0, 0, w, h);
          }
        );
        context.clearRect(0, 0, w, h);
      }

      if (fill) {
        drawBackground(stageContext, node);
      }
      clipFrame(stageContext, w, h, () => {
        for (const child of renderNodes) {
          if (child.isSeedeBadge) {
            seedeBadge = child;
            continue;
          }
          renderNode(stageContext, child);
        }
      });
    }
  }

  if (seedeBadge) {
    // In direct rendering path, draw logo here. In cache path we draw on main context after blitting.
    const padding = 16;
    seedeBadge.x = node.w - seedeBadge.w - padding;
    seedeBadge.y = node.h - seedeBadge.h - padding;
    renderNode(stageContext, seedeBadge);
  }

  context.translate(-x, -y);
}

function clipFrame(stageContext, w, h, render) {
  const { context } = stageContext;
  context.save();
  context.beginPath();
  context.rect(0, 0, w, h);
  context.closePath();
  context.clip();
  render();
  context.restore();
}

/**
 * Draw text centered at a given x with wrapping and ellipsis within maxWidth and maxLines
 */
function drawWrappedCenteredText(ctx, text, centerX, startY, maxWidth, lineHeight, maxLines) {
  if (!text || maxWidth <= 0 || maxLines <= 0) return;

  const savedAlign = ctx.textAlign;
  const savedBaseline = ctx.textBaseline;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  const ellipsis = '…';
  const ellipsisWidth = ctx.measureText(ellipsis).width;

  const lines = [];
  let current = '';

  for (let i = 0; i < text.length; i++) {
    const next = current + text[i];
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      if (lines.length === maxLines - 1) {
        // Last line: fill as much as possible and add ellipsis
        let fit = '';
        for (let j = 0; j < current.length; j++) {
          const candidate = current.slice(0, j + 1);
          if (ctx.measureText(candidate).width + ellipsisWidth <= maxWidth) {
            fit = candidate;
          } else {
            break;
          }
        }
        ctx.fillText(fit + ellipsis, centerX, startY + lineHeight * lines.length);
        ctx.textAlign = savedAlign;
        ctx.textBaseline = savedBaseline;
        return;
      }
      lines.push(current);
      current = text[i];
    }
  }

  if (current) lines.push(current);
  if (lines.length > maxLines) {
    // Clamp overflow with ellipsis on the last visible line
    const visible = lines.slice(0, maxLines);
    let last = visible[visible.length - 1];
    while (last.length > 0 && ctx.measureText(last).width + ellipsisWidth > maxWidth) {
      last = last.slice(0, -1);
    }
    visible[visible.length - 1] = last + ellipsis;
    for (let i = 0; i < visible.length; i++) {
      ctx.fillText(visible[i], centerX, startY + lineHeight * i);
    }
  } else {
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], centerX, startY + lineHeight * i);
    }
  }

  ctx.textAlign = savedAlign;
  ctx.textBaseline = savedBaseline;
}

function drawEmptyState(stageContext, w, h) {
  const { context } = stageContext;
  drawShadow(
    stageContext,
    {
      x: 0,
      y: 0,
      w,
      h,
      shadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
    },
    () => {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, w, h);
    }
  );

  // Add guiding text in the center of the canvas
  context.fillStyle = '#cccccc';
  context.font = '48px Arial, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText('通过 AI 生成快速开始', w / 2, h / 2 - 32);
  context.fillText('点击下方元素栏添加到画板', w / 2, h / 2 + 32);
}

const loadingTextI18N = {
  zh: {
    tips: [
      '字体提示：尝试不同字体组合增强视觉层次感',
      '风格技巧：使用风格提示词词可以提升设计质感',
      '排版技巧：正文与标题字体搭配可提升专业感',
      '字体选择：Sans-serif字体适合现代设计，Serif字体增添优雅',
      '字体大小：遵循比例原则，标题与正文保持3:1比例',
      '创意提示：使用我们的模板库获取设计灵感',
      '专业技巧：调整大小可以适配不同社交媒体平台',
      '效率提示：右键菜单包含更多高级编辑选项',
      '设计原则：尝试不同的背景渐变提升视觉效果',
      '创作建议：添加 Logo 让你的海报更专业',
      '布局技巧：使用网格对齐功能让元素排列更整齐',
      '配色技巧：提供色值可以获得更准确的颜色控制',
      '导出提示：PDF 适合打印，PNG、JPG适合网络分享',
    ],
    steps: ['播种你的创意...', '生成中...', '即将完成...'],
  },
  ja: {
    tips: [
      'フォントのヒント：異なるフォントの組み合わせで視覚的階層を強化',
      'スタイルのコツ：スタイルキーワードを使用してデザイン品質を向上',
      'レイアウトのコツ：本文とタイトルフォントの組み合わせでプロフェッショナル感を向上',
      'フォント選択：Sans-serifフォントはモダンデザインに適し、Serifフォントは優雅さを追加',
      'フォントサイズ：比例の原則に従い、タイトルと本文の比率を3:1に保つ',
      '創造性のヒント：テンプレートライブラリを使用してデザインのインスピレーションを得る',
      'プロのコツ：異なるソーシャルメディアプラットフォームに合わせてサイズを調整',
      '効率のヒント：右クリックメニューにはより高度な編集オプションが含まれています',
      'デザインの原則：異なる背景グラデーションを試して視覚効果を向上',
      '制作アドバイス：ロゴを追加してポスターをよりプロフェッショナルに',
      'レイアウトのコツ：グリッド配置機能を使用して要素をより整然と配列',
      '配色のコツ：色値を提供してより正確な色彩制御を実現',
      'エクスポートのヒント：PDFは印刷に適し、PNG・JPGはウェブ共有に適している',
    ],
    steps: ['あなたの創造性を育んでいます...', '生成中...', 'もうすぐ完成...'],
  },
  en: {
    tips: [
      'Font Tips: Try different font combinations to enhance visual hierarchy',
      'Style Tips: Use style keywords to improve design quality',
      'Typography Tips: Pairing body and title fonts enhances professionalism',
      'Font Selection: Sans-serif fonts suit modern design, Serif fonts add elegance',
      'Font Size: Follow proportional principles, maintain 3:1 ratio between titles and body text',
      'Creative Tips: Use our template library for design inspiration',
      'Professional Tips: Resize to fit different social media platforms',
      'Efficiency Tips: Right-click menu contains more advanced editing options',
      'Design Principles: Try different background gradients to enhance visual effects',
      'Creative Advice: Add a Logo to make your poster more professional',
      'Layout Tips: Use grid alignment to arrange elements more neatly',
      'Color Tips: Provide color values for more accurate color control',
      'Export Tips: PDF is suitable for printing, PNG and JPG are suitable for web sharing',
    ],
    steps: ['Seeding your creativity...', 'Generating...', 'Almost ready...'],
  },
};

function renderLoading(stageContext, node) {
  const { context } = stageContext;
  // Slow down time slightly for a more graceful, mesmerizing animation
  const now =
    stageContext && stageContext.api && typeof stageContext.api.now === 'function'
      ? stageContext.api.now()
      : performance && performance.now
        ? performance.now()
        : Date.now();

  const time = (now / 1000) * 0.7;
  const { w, h } = node;
  const centerX = w / 2;
  const centerY = h / 2;
  // The base radius of the sphere. Reduced slightly to prevent glow from being clipped by rounded corners.
  const baseRadius = Math.min(w, h) * 0.21;

  const loadingTexts = loadingTextI18N[stageContext.locale] || loadingTextI18N.en;
  const steps = loadingTexts.steps;
  const tips = loadingTexts.tips;

  // --- Background ---
  // A deep, cosmic background creates a high-tech, mysterious atmosphere.
  // The original drawShadow is kept for its defined behavior. The clipping issue is addressed by reducing the sphere's size.
  drawShadow(
    stageContext,
    {
      x: 0,
      y: 0,
      w,
      h,
      // A deeper, softer shadow enhances the illusion of depth
      shadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
    },
    () => {
      // Cache the background gradient on the node to improve performance by avoiding recreation on every frame.
      if (!node._bgGradient || node._bgGradient.w !== w) {
        const bgGradient = context.createRadialGradient(
          centerX,
          centerY,
          0,
          centerX,
          centerY,
          w * 0.7
        );
        bgGradient.addColorStop(0, '#0d1033'); // Center: a deep, dark indigo
        bgGradient.addColorStop(0.6, '#02020a'); // Mid-point: near black
        bgGradient.addColorStop(1, '#000000'); // Edge: pure black for vignetting
        node._bgGradient = bgGradient;
        node._bgGradient.w = w; // Store width to invalidate cache on resize
      }
      context.fillStyle = node._bgGradient;
      context.beginPath();
      context.roundRect(0, 0, w, h, 32); // Softer corners for a premium feel
      context.fill();
    }
  );

  context.save();
  context.translate(centerX, centerY);

  // --- Central Glow ---
  // A single, efficient glow effect replaces the expensive per-band shadows and the separate core pulse.
  const glowPulse = 0.9 + 0.1 * Math.sin(time * 2);
  const glowRadius = baseRadius * 1.3 * glowPulse;
  const glowGradient = context.createRadialGradient(0, 0, 0, 0, 0, glowRadius);
  const glowHue = (200 + time * 30) % 360;
  glowGradient.addColorStop(0, `hsla(${glowHue}, 100%, 80%, 0.3)`);
  glowGradient.addColorStop(0.5, `hsla(${(glowHue + 40) % 360}, 100%, 70%, 0.1)`);
  glowGradient.addColorStop(1, `hsla(${(glowHue + 80) % 360}, 100%, 60%, 0)`);
  context.fillStyle = glowGradient;
  context.beginPath();
  context.arc(0, 0, glowRadius, 0, Math.PI * 2);
  context.fill();

  // --- Mobius Sphere ---
  // Renders a complex, interwoven sphere of light, optimized for performance.
  const numBands = 3; // Number of bands for the structure
  const pointsPerBand = 60; // Reduced from 60 for better performance

  // 'lighter' blending is key to the glowing, high-energy aesthetic.
  context.globalCompositeOperation = 'lighter';

  // A slow, continuous rotation of the entire structure adds dynamism.
  const globalRotation = time * 0.2;

  for (let i = 0; i < numBands; i++) {
    const bandProgress = i / numBands;

    // Each band is rotated on the Y-axis to form the spherical shape.
    const bandRotation = bandProgress * Math.PI;

    // Wave parameters define the life-like oscillation of the bands.
    const waveSpeed = (1.2 + bandProgress) * 1.5;
    const waveFrequency = 4 + i; // Each band has a unique frequency
    const waveAmplitude = baseRadius * 0.12;

    // Perspective squashes the bands into ellipses, creating the 3D illusion.
    const perspective = 0.6;

    // A vibrant, cycling color scheme from cyan to magenta.
    const hue = (180 + bandProgress * 150 + time * 40) % 360;
    const gradient = context.createLinearGradient(-baseRadius, 0, baseRadius, 0);
    gradient.addColorStop(0, `hsla(${hue}, 100%, 80%, 0)`);
    gradient.addColorStop(0.2, `hsla(${hue}, 100%, 65%, 1)`);
    gradient.addColorStop(0.8, `hsla(${hue + 50}, 100%, 65%, 1)`);
    gradient.addColorStop(1, `hsla(${hue + 50}, 100%, 80%, 0)`);

    context.strokeStyle = gradient;
    // Set a fixed line width. The original implementation had a bug where only the last point's width was used.
    context.lineWidth = 2.5;
    // Removed expensive shadow blur from the loop. The single glow effect is much more performant.

    context.beginPath();

    for (let j = 0; j <= pointsPerBand; j++) {
      const pointProgress = j / pointsPerBand;
      const angle = pointProgress * Math.PI * 2;

      // The wave makes the bands appear to flow and breathe.
      const waveOffset = Math.sin(angle * waveFrequency + time * waveSpeed) * waveAmplitude;
      const radius = baseRadius + waveOffset;

      // Calculate the base point on a 2D ellipse.
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius * perspective;

      // Apply the band's individual static rotation.
      const rotatedX = x * Math.cos(bandRotation) - y * Math.sin(bandRotation);
      const rotatedY = x * Math.sin(bandRotation) + y * Math.cos(bandRotation);

      // Apply the global rotation to the entire sphere.
      const finalX = rotatedX * Math.cos(globalRotation) - rotatedY * Math.sin(globalRotation);
      const finalY = rotatedX * Math.sin(globalRotation) + rotatedY * Math.cos(globalRotation);

      if (j === 0) {
        context.moveTo(finalX, finalY);
      } else {
        context.lineTo(finalX, finalY);
      }
    }
    context.stroke();
  }

  context.restore(); // Restore context (removes translate, composite op, etc.)

  // --- Text Rendering ---
  // Text is designed to be clean, modern, and legible against the complex background.
  const defaultStageText = steps[Math.floor(time / 4) % steps.length];
  const stageText = node.loadingText ? `${node.loadingText}...` : defaultStageText;
  const fontSize = Math.max(18, Math.min(w * 0.05, 38));

  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `500 ${fontSize}px 'Inter', 'SF Pro Display', 'Helvetica Neue', sans-serif`;

  // Add a subtle glow to the text to make it pop from the background.
  context.shadowColor = 'rgba(173, 216, 230, 0.5)';
  context.shadowBlur = 10;

  // Pulsating text opacity for a "breathing" effect.
  context.globalAlpha = 0.7 + 0.3 * Math.sin(time * 2.5);
  context.fillStyle = '#e0e7ff'; // A light, cool color to complement the orb.
  context.fillText(stageText, centerX, centerY + baseRadius * 1.7);
  context.globalAlpha = 1;
  context.shadowBlur = 0; // Reset shadow for the tip text

  // Tip text (wrapped & clamped to stay inside frame)
  const tipIndex = Math.floor(time / 5) % tips.length;
  const tipText = tips[tipIndex];
  const tipFontSize = Math.max(12, Math.min(w * 0.03, 28));
  context.font = `400 ${tipFontSize}px 'Inter', 'SF Pro Display', 'Helvetica Neue', sans-serif`;
  context.fillStyle = 'rgba(224, 231, 255, 0.7)';

  // Clip to rounded frame to prevent any accidental overflow due to shadows or long text
  context.save();
  context.beginPath();
  context.roundRect(0, 0, w, h, 32);
  context.clip();

  // Compute wrapping metrics
  const horizontalPadding = Math.max(16, Math.min(w, h) * 0.06);
  const maxTextWidth = Math.max(0, w - horizontalPadding * 2);
  const lineHeight = Math.ceil(tipFontSize * 1.35);
  const startY = centerY + baseRadius * 1.7 + fontSize * 1.4 + Math.max(6, tipFontSize * 0.25);
  const availableHeight = Math.max(0, h - startY - horizontalPadding);
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

  context.textBaseline = 'top';
  drawWrappedCenteredText(context, tipText, centerX, startY, maxTextWidth, lineHeight, maxLines);

  // Subtle shimmer effect for frame - optimized diagonal movement
  const shimmerCycle = 4000; // Slower, more elegant cycle
  const shimmerPhase = (now % shimmerCycle) / shimmerCycle;

  // Create a subtle diagonal shimmer that flows naturally across the frame
  const shimmerGradient = context.createLinearGradient(
    -w * 0.3, // Start from top-left
    -h * 0.3,
    w * 1.3, // End at bottom-right for diagonal flow
    h * 1.3
  );

  // Animated shimmer position with subtle movement
  const shimmerPos = (shimmerPhase * 1.2) % 1;
  const shimmerWidth = 0.15; // Slightly narrower shimmer band

  // Very subtle shimmer colors - barely visible
  shimmerGradient.addColorStop(Math.max(0, shimmerPos - shimmerWidth), 'rgba(255, 255, 255, 0.01)');
  shimmerGradient.addColorStop(shimmerPos, 'rgba(255, 255, 255, 0.08)'); // Slightly more subtle
  shimmerGradient.addColorStop(Math.min(1, shimmerPos + shimmerWidth), 'rgba(255, 255, 255, 0.01)');

  // Apply very subtle shimmer overlay
  context.fillStyle = shimmerGradient;
  context.fillRect(0, 0, w, h);

  context.restore();
}
